import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { LogoMark, OrnamentDivider } from "@/components/suffa/Ornaments";
import { useAuth } from "@/lib/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/lib/i18n";
import "@/pages/Auth.css";

export const ADMIN_ROLES = ["admin", "super_admin"];

const AdminLogin = () => {
  const { t } = useLanguage();
  const { user, authReady, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Already-authenticated admins go straight to the dashboard.
  useEffect(() => {
    if (authReady && user && ADMIN_ROLES.includes(user.role)) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [authReady, user, navigate]);

  useEffect(() => {
    document.title = "Admin Login | Suffa Matrimony";
  }, []);

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Please enter your admin email and password.");
      return;
    }
    setLoading(true);
    try {
      const loggedIn = await login(email, password);
      if (loggedIn && ADMIN_ROLES.includes(loggedIn.role)) {
        navigate("/admin/dashboard", { replace: true });
      } else {
        setError("This account does not have admin privileges.");
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ap-denied" data-testid="admin-login-page">
      <div className="ap-card" style={{ maxWidth: 440, textAlign: "center", padding: "32px 28px" }}>
        <a href="/" className="auth-logo" aria-label="Suffa Matrimony home" style={{ display: "inline-flex" }}>
          <LogoMark size={40} />
        </a>
        <div style={{ margin: "10px 0 4px" }}>
          <OrnamentDivider />
        </div>
        <h1 className="ap-title" data-testid="admin-login-title">Admin Login</h1>
        <p className="ap-sub" style={{ marginBottom: 22 }}>
          {t("Restricted area — authorized Suffa Matrimony administrators only.")}
        </p>

        <form className="auth-form" noValidate onSubmit={onSubmit} style={{ textAlign: "left" }}>
          {error && (
            <p className="ap-error" data-testid="admin-login-error" role="alert">
              {t(error)}
            </p>
          )}

          <div className="ap-field">
            <label className="ap-label" htmlFor="admin-login-email">Email Address</label>
            <input
              id="admin-login-email"
              type="email"
              className="ap-input"
              placeholder="admin@suffamatrimony.com"
              autoComplete="email"
              value={email}
              data-testid="admin-login-email"
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="ap-field">
            <label className="ap-label" htmlFor="admin-login-password">Password</label>
            <div className="auth-password">
              <input
                id="admin-login-password"
                type={showPw ? "text" : "password"}
                className="ap-input"
                placeholder="Admin password"
                autoComplete="current-password"
                value={password}
                data-testid="admin-login-password"
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="auth-eye"
                data-testid="admin-login-password-toggle"
                aria-label={showPw ? "Hide password" : "Show password"}
                onClick={() => setShowPw((v) => !v)}
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-solid btn-lg"
            data-testid="admin-login-submit"
            disabled={loading}
            style={{ width: "100%", marginTop: 6 }}
          >
            {loading ? t("Signing in…") : t("Login to Admin Panel")}
          </button>
        </form>

        <p className="ap-note" style={{ marginTop: 16 }}>
          Not an administrator?{" "}
          <Link to="/login" className="auth-footer-link" data-testid="admin-login-to-site">
            Login as a member
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
